import React, { useState, useEffect } from 'react';
import { User, Award, Save, AlertCircle, Brain, Sparkles } from 'lucide-react';
import { Modal, ModalActions, TagInput, Alert } from '../shared';
import api from '../../services/api';
import FormSectionTabs from './FormSectionTabs';

const EditCandidateModal = ({ candidate, open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skills: [],
    cvRole: '',
    cvExperienceYears: '',
    cvTrigramme: '',
    cvCity: '',
    cvPostalCode: '',
    cvCountry: '',
    cvAvailability: '',
    cvEducation: '',
    cv1stExperienceTitle: '',
    cv1stExperienceCompany: '',
    cv1stExperienceStartDate: '',
    cv1stExperienceEndDate: '',
    cv1stExperienceLocation: '',
    cv1stExperienceResponsibilities: [],
    cv2ndExperienceTitle: '',
    cv2ndExperienceCompany: '',
    cv2ndExperienceStartDate: '',
    cv2ndExperienceEndDate: '',
    cv2ndExperienceLocation: '',
    cv3rdExperienceTitle: '',
    cv3rdExperienceCompany: '',
    cv3rdExperienceStartDate: '',
    cv3rdExperienceEndDate: '',
    cv3rdExperienceLocation: '',
    cvHardSkills: [],
    cvSoftSkills: [],
    cvKeyStrengths: [],
    cvLanguages: [],
    cvMissingSkills: [],
    cvUrl: '',
    expectedTjm: '',
    salesforceId: '',
    needId: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('informations');

  // Pre-fill form with candidate data when modal opens
  useEffect(() => {
    if (candidate && open) {
      // Helper function to parse JSON fields safely
      const parseJsonField = (field) => {
        if (!field) return [];
        if (typeof field === 'string') {
          try {
            return JSON.parse(field);
          } catch {
            return [];
          }
        }
        return Array.isArray(field) ? field : [];
      };

      setFormData({
        name: candidate.name || '',
        email: candidate.email || '',
        phone: candidate.phone || '',
        skills: candidate.skills || [],
        cvRole: candidate.cvRole || candidate.cv_role || '',
        cvExperienceYears: candidate.cvExperienceYears || candidate.cv_experience_years || '',
        cvTrigramme: candidate.cvTrigramme || candidate.cv_trigramme || '',
        cvCity: candidate.cvCity || candidate.cv_city || '',
        cvPostalCode: candidate.cvPostalCode || candidate.cv_postal_code || '',
        cvCountry: candidate.cvCountry || candidate.cv_country || '',
        cvAvailability: candidate.cvAvailability || candidate.cv_availability || '',
        cvEducation: candidate.cvEducation || candidate.cv_education || '',
        cv1stExperienceTitle: candidate.cv1stExperienceTitle || candidate.cv_1st_experience_title || '',
        cv1stExperienceCompany: candidate.cv1stExperienceCompany || candidate.cv_1st_experience_company || '',
        cv1stExperienceStartDate: candidate.cv1stExperienceStartDate || candidate.cv_1st_experience_start_date || '',
        cv1stExperienceEndDate: candidate.cv1stExperienceEndDate || candidate.cv_1st_experience_end_date || '',
        cv1stExperienceLocation: candidate.cv1stExperienceLocation || candidate.cv_1st_experience_location || '',
        cv1stExperienceResponsibilities: parseJsonField(candidate.cv1stExperienceResponsibilitiesJson || candidate.cv_1st_experience_responsibilities_json),
        cv2ndExperienceTitle: candidate.cv2ndExperienceTitle || candidate.cv_2nd_experience_title || '',
        cv2ndExperienceCompany: candidate.cv2ndExperienceCompany || candidate.cv_2nd_experience_company || '',
        cv2ndExperienceStartDate: candidate.cv2ndExperienceStartDate || candidate.cv_2nd_experience_start_date || '',
        cv2ndExperienceEndDate: candidate.cv2ndExperienceEndDate || candidate.cv_2nd_experience_end_date || '',
        cv2ndExperienceLocation: candidate.cv2ndExperienceLocation || candidate.cv_2nd_experience_location || '',
        cv3rdExperienceTitle: candidate.cv3rdExperienceTitle || candidate.cv_3rd_experience_title || '',
        cv3rdExperienceCompany: candidate.cv3rdExperienceCompany || candidate.cv_3rd_experience_company || '',
        cv3rdExperienceStartDate: candidate.cv3rdExperienceStartDate || candidate.cv_3rd_experience_start_date || '',
        cv3rdExperienceEndDate: candidate.cv3rdExperienceEndDate || candidate.cv_3rd_experience_end_date || '',
        cv3rdExperienceLocation: candidate.cv3rdExperienceLocation || candidate.cv_3rd_experience_location || '',
        cvHardSkills: candidate.cvAnalysis?.hardskills || parseJsonField(candidate.cvHardSkillsJson || candidate.cv_hard_skills_json),
        cvSoftSkills: candidate.cvAnalysis?.softskills || parseJsonField(candidate.cvSoftSkillsJson || candidate.cv_soft_skills_json),
        cvKeyStrengths: candidate.cvAnalysis?.keystrengths || parseJsonField(candidate.cvKeyStrengthsJson || candidate.cv_key_strengths_json),
        cvLanguages: candidate.cvAnalysis?.languages || parseJsonField(candidate.cvLanguagesJson || candidate.cv_languages_json),
        cvMissingSkills: candidate.cvAnalysis?.missingskills || parseJsonField(candidate.cvMissingSkillsJson || candidate.cv_missing_skills_json),
        cvUrl: candidate.cvUrl || '',
        expectedTjm: candidate.expectedTjm || '',
        salesforceId: candidate.salesforceId || '',
        needId: candidate.needId || ''
      });
      setError('');
      setActiveSection('informations');
    }
  }, [candidate, open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], value]
    }));
  };

  const handleRemoveTag = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(item => item !== value)
    }));
  };

  const validateForm = () => {
    // Reset any previous errors
    setError('');
    
    // Check required fields
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (formData.cvUrl.trim()) {
      try {
        new URL(formData.cvUrl);
      } catch {
        setError('Please enter a valid CV URL');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        cvUrl: formData.cvUrl,
        skills: formData.skills,
        expectedTjm: formData.expectedTjm,
        salesforceId: formData.salesforceId,
        // Include CV analysis fields
        cvTrigramme: formData.cvTrigramme,
        cvRole: formData.cvRole,
        cvExperienceYears: formData.cvExperienceYears,
        cvAvailability: formData.cvAvailability,
        cvEducation: formData.cvEducation,
        cvCity: formData.cvCity,
        cvPostalCode: formData.cvPostalCode,
        cvCountry: formData.cvCountry,
        cvHardSkillsJson: formData.cvHardSkills ? JSON.stringify(formData.cvHardSkills) : null,
        cvSoftSkillsJson: formData.cvSoftSkills ? JSON.stringify(formData.cvSoftSkills) : null,
        cvLanguagesJson: formData.cvLanguages ? JSON.stringify(formData.cvLanguages) : null,
        cvKeyStrengthsJson: formData.cvKeyStrengths ? JSON.stringify(formData.cvKeyStrengths) : null,
        cvMissingSkillsJson: formData.cvMissingSkills ? JSON.stringify(formData.cvMissingSkills) : null,
        cv1stExperienceCompany: formData.cv1stExperienceCompany,
        cv1stExperienceTitle: formData.cv1stExperienceTitle,
        cv1stExperienceLocation: formData.cv1stExperienceLocation,
        cv1stExperienceStartDate: formData.cv1stExperienceStartDate,
        cv1stExperienceEndDate: formData.cv1stExperienceEndDate,
        cv1stExperienceResponsibilitiesJson: JSON.stringify(formData.cv1stExperienceResponsibilities || [])
      };
      
      // Remove undefined/null/empty values to avoid validation issues
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined || updateData[key] === null || updateData[key] === '') {
          delete updateData[key];
        }
      });
      
      const response = await api.patch(`/candidates/${candidate.id}`, updateData);
      const updatedCandidate = response.data;
      
      onSuccess?.(updatedCandidate);
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update candidate';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Check if this is a post-AI analysis edit (missing email)
  const isPostAIAnalysis = candidate && (!candidate.email || candidate.email.trim() === '');
  const hasAIAnalysisData = candidate && (candidate.cvAnalysis || candidate.cvRole || candidate.cvHardSkillsJson);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isPostAIAnalysis && hasAIAnalysisData ? 'Compléter le profil' : 'Modifier le candidat'}
      subtitle={isPostAIAnalysis && hasAIAnalysisData
        ? 'AI analysis complete! Please provide missing contact information.'
        : 'Update candidate information'}
      icon={isPostAIAnalysis && hasAIAnalysisData ? Brain : User}
      maxWidth="max-w-2xl"
      headerGradient={isPostAIAnalysis && hasAIAnalysisData
        ? 'from-purple-50 to-blue-50'
        : 'from-blue-50 to-indigo-50'}
    >
          {/* ✅ NEW: AI Analysis completion notice */}
          {isPostAIAnalysis && hasAIAnalysisData && (
            <div className="mx-6 mt-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-purple-900 mb-1">AI Analysis Complete!</h4>
                  <p className="text-sm text-purple-800">
                    We've successfully analyzed the CV and extracted comprehensive candidate information. 
                    Please provide the missing email address to complete the candidate profile.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} noValidate className="p-6 space-y-6">
             {error && (
               <Alert variant="error">{error}</Alert>
             )}

            <FormSectionTabs
              tabs={[
                { id: 'informations', label: 'Informations' },
                { id: 'expertise', label: 'Parcours & expertise' },
                { id: 'liens', label: 'Liens' },
              ]}
              activeTab={activeSection}
              onChange={setActiveSection}
              label="Rubriques du candidat"
              idPrefix="edit-candidate-sections"
            />

            {/* Basic Information */}
            <div id="edit-candidate-sections-informations-panel" role="tabpanel" aria-labelledby="edit-candidate-sections-informations-tab" className={activeSection === 'informations' ? 'space-y-4' : 'hidden'}>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <User className="mr-2" size={20} />
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="edit-candidate-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    id="edit-candidate-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="edit-candidate-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  {/* ✅ NEW: Highlight email field for post-AI analysis */}
                  {isPostAIAnalysis && (
                    <p className="text-xs text-purple-600 mb-1 flex items-center gap-1">
                      <AlertCircle size={12} /> Required for candidate completion
                    </p>
                  )}
                  <input
                    id="edit-candidate-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                      isPostAIAnalysis && !formData.email
                        ? 'border-purple-300 bg-purple-50 focus:ring-purple-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="edit-candidate-phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    id="edit-candidate-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Skills */}
            <div className={activeSection === 'informations' ? 'space-y-4' : 'hidden'}>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Award className="mr-2" size={20} />
                Skills
              </h3>
              <TagInput
                items={formData.skills}
                onAdd={(value) => handleAddTag('skills', value)}
                onRemove={(value) => handleRemoveTag('skills', value)}
                placeholder="Add a skill..."
                color="blue"
              />
            </div>

            <div className={activeSection === 'informations' ? '' : 'hidden'}>
              <label htmlFor="edit-candidate-tjm" className="mb-1 block text-sm font-medium text-gray-700">TJM attendu</label>
              <input id="edit-candidate-tjm" type="number" min="0" value={formData.expectedTjm} onChange={(e) => handleInputChange('expectedTjm', e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500" />
            </div>

            {/* Professional Information */}
            <div className={activeSection === 'informations' ? 'space-y-4' : 'hidden'}>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Award className="mr-2" size={20} />
                Professional Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Role
                  </label>
                  <input
                    type="text"
                    value={formData.cvRole}
                    onChange={(e) => handleInputChange('cvRole', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Senior Developer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Experience Years
                  </label>
                  <input
                    type="text"
                    value={formData.cvExperienceYears}
                    onChange={(e) => handleInputChange('cvExperienceYears', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 5 years"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trigramme
                  </label>
                  <input
                    type="text"
                    value={formData.cvTrigramme}
                    onChange={(e) => handleInputChange('cvTrigramme', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., ABC"
                    maxLength={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Availability
                  </label>
                  <input
                    type="text"
                    value={formData.cvAvailability}
                    onChange={(e) => handleInputChange('cvAvailability', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Immediately, 2 weeks notice"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Education
                  </label>
                  <input
                    type="text"
                    value={formData.cvEducation}
                    onChange={(e) => handleInputChange('cvEducation', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Master's in Computer Science"
                  />
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className={activeSection === 'informations' ? 'space-y-4' : 'hidden'}>
              <h3 className="text-lg font-semibold text-gray-900">Location</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.cvCity}
                    onChange={(e) => handleInputChange('cvCity', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={formData.cvPostalCode}
                    onChange={(e) => handleInputChange('cvPostalCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.cvCountry}
                    onChange={(e) => handleInputChange('cvCountry', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Technical Skills */}
            <div id="edit-candidate-sections-expertise-panel" role="tabpanel" aria-labelledby="edit-candidate-sections-expertise-tab" className={activeSection === 'expertise' ? 'space-y-4' : 'hidden'}>
              <h3 className="text-lg font-semibold text-gray-900">Technical Skills</h3>
              <TagInput
                items={formData.cvHardSkills}
                onAdd={(value) => handleAddTag('cvHardSkills', value)}
                onRemove={(value) => handleRemoveTag('cvHardSkills', value)}
                placeholder="Add a technical skill..."
                color="blue"
              />
            </div>

            {/* Soft Skills */}
            <div className={activeSection === 'expertise' ? 'space-y-4' : 'hidden'}>
              <h3 className="text-lg font-semibold text-gray-900">Soft Skills</h3>
              <TagInput
                items={formData.cvSoftSkills}
                onAdd={(value) => handleAddTag('cvSoftSkills', value)}
                onRemove={(value) => handleRemoveTag('cvSoftSkills', value)}
                placeholder="Add a soft skill..."
                color="green"
              />
            </div>

            {/* Key Strengths */}
            <div className={activeSection === 'expertise' ? 'space-y-4' : 'hidden'}>
              <h3 className="text-lg font-semibold text-gray-900">Key Strengths</h3>
              <TagInput
                items={formData.cvKeyStrengths}
                onAdd={(value) => handleAddTag('cvKeyStrengths', value)}
                onRemove={(value) => handleRemoveTag('cvKeyStrengths', value)}
                placeholder="Add a key strength..."
                color="purple"
              />
            </div>

            {/* Languages */}
            <div className={activeSection === 'expertise' ? 'space-y-4' : 'hidden'}>
              <h3 className="text-lg font-semibold text-gray-900">Languages</h3>
              <TagInput
                items={formData.cvLanguages}
                onAdd={(value) => handleAddTag('cvLanguages', value)}
                onRemove={(value) => handleRemoveTag('cvLanguages', value)}
                placeholder="Add a language..."
                color="indigo"
              />
            </div>

            {/* Latest Experience */}
            <div className={activeSection === 'expertise' ? 'space-y-4' : 'hidden'}>
              <h3 className="text-lg font-semibold text-gray-900">Latest Experience</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={formData.cv1stExperienceTitle}
                    onChange={(e) => handleInputChange('cv1stExperienceTitle', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    value={formData.cv1stExperienceCompany}
                    onChange={(e) => handleInputChange('cv1stExperienceCompany', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="text"
                    value={formData.cv1stExperienceStartDate}
                    onChange={(e) => handleInputChange('cv1stExperienceStartDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., January 2020"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="text"
                    value={formData.cv1stExperienceEndDate}
                    onChange={(e) => handleInputChange('cv1stExperienceEndDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Present, December 2023"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.cv1stExperienceLocation}
                    onChange={(e) => handleInputChange('cv1stExperienceLocation', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Responsibilities */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key Responsibilities
                </label>
                <TagInput
                  items={formData.cv1stExperienceResponsibilities}
                  onAdd={(value) => handleAddTag('cv1stExperienceResponsibilities', value)}
                  onRemove={(value) => handleRemoveTag('cv1stExperienceResponsibilities', value)}
                  placeholder="Add a responsibility..."
                  color="gray"
                />
              </div>
            </div>

            <div id="edit-candidate-sections-liens-panel" role="tabpanel" aria-labelledby="edit-candidate-sections-liens-tab" className={activeSection === 'liens' ? 'grid gap-4 md:grid-cols-2' : 'hidden'}>
              <div className="md:col-span-2">
                <label htmlFor="edit-candidate-cv-url" className="mb-1 block text-sm font-medium text-gray-700">URL du CV *</label>
                <input id="edit-candidate-cv-url" type="url" value={formData.cvUrl} onChange={(e) => handleInputChange('cvUrl', e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label htmlFor="edit-candidate-salesforce-id" className="mb-1 block text-sm font-medium text-gray-700">Identifiant Salesforce</label>
                <input id="edit-candidate-salesforce-id" value={formData.salesforceId} onChange={(e) => handleInputChange('salesforceId', e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>

            {/* Form Actions */}
            <ModalActions
              onCancel={onClose}
              loading={loading}
              submitLabel={isPostAIAnalysis && hasAIAnalysisData ? 'Complete Profile' : 'Update Candidate'}
              loadingLabel="Updating..."
              submitIcon={Save}
            />
          </form>
    </Modal>
  );
};

export default EditCandidateModal;
